/**
 * Bill Williams Alligator
 *
 * Three smoothed moving averages (SMMA) with different periods and offsets:
 * - Jaw (Blue): SMMA(13) shifted 8 bars ahead
 * - Teeth (Red): SMMA(8) shifted 5 bars ahead
 * - Lips (Green): SMMA(5) shifted 3 bars ahead
 * When lines intertwine = sleeping (no trend). When spread apart = feeding.
 *
 * Category: Advanced | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

function computeSMMA(values: number[], period: number): number[] {
  const result: number[] = [];
  let smma = NaN;
  for (let i = 0; i < values.length; i++) {
    if (i < period - 1) { result.push(NaN); continue; }
    if (i === period - 1) {
      let sum = 0; for (let j = 0; j < period; j++) sum += values[j];
      smma = sum / period;
    } else {
      smma = (smma * (period - 1) + values[i]) / period;
    }
    result.push(smma);
  }
  return result;
}

export const alligatorIndicator: IndicatorDefinition = {
  id: "alligator",
  name: "Williams Alligator",
  shortName: "ALLIGATOR",
  category: "advanced",
  pane: "overlay",
  params: [
    { key: "jawPeriod", label: "Jaw Period", type: "number", default: 13, min: 2, max: 100 },
    { key: "jawOffset", label: "Jaw Offset", type: "number", default: 8, min: 0, max: 50 },
    { key: "teethPeriod", label: "Teeth Period", type: "number", default: 8, min: 2, max: 100 },
    { key: "teethOffset", label: "Teeth Offset", type: "number", default: 5, min: 0, max: 50 },
    { key: "lipsPeriod", label: "Lips Period", type: "number", default: 5, min: 2, max: 100 },
    { key: "lipsOffset", label: "Lips Offset", type: "number", default: 3, min: 0, max: 50 },
  ],
  plots: [
    { key: "jaw", label: "Jaw", color: "#2196F3", style: "line", lineWidth: 2 },
    { key: "teeth", label: "Teeth", color: "#F44336", style: "line", lineWidth: 2 },
    { key: "lips", label: "Lips", color: "#4CAF50", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { jawPeriod, jawOffset, teethPeriod, teethOffset, lipsPeriod, lipsOffset } = params;
    const hl2 = candles.map(c => (c.high + c.low) / 2);

    const jawSmma = computeSMMA(hl2, jawPeriod);
    const teethSmma = computeSMMA(hl2, teethPeriod);
    const lipsSmma = computeSMMA(hl2, lipsPeriod);

    const jaw: { time: number; value: number }[] = [];
    const teeth: { time: number; value: number }[] = [];
    const lips: { time: number; value: number }[] = [];

    for (let i = 0; i < candles.length; i++) {
      const ji = i - jawOffset;
      const ti = i - teethOffset;
      const li = i - lipsOffset;

      if (ji >= 0 && !isNaN(jawSmma[ji])) {
        jaw.push({ time: candles[i].time, value: jawSmma[ji] });
      }
      if (ti >= 0 && !isNaN(teethSmma[ti])) {
        teeth.push({ time: candles[i].time, value: teethSmma[ti] });
      }
      if (li >= 0 && !isNaN(lipsSmma[li])) {
        lips.push({ time: candles[i].time, value: lipsSmma[li] });
      }
    }

    return [
      { key: "jaw", data: jaw },
      { key: "teeth", data: teeth },
      { key: "lips", data: lips },
    ];
  },
};
